import { useState } from 'react';
import { format } from 'date-fns';
import { Plus, FileText, Trash2, ExternalLink, AlertTriangle, CheckCircle, Clock, XCircle, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { 
  useTaxExemptions, 
  useCreateTaxExemption, 
  useDeleteTaxExemption,
  US_STATES,
  EXEMPTION_TYPES,
  TaxExemption
} from '@/hooks/useTaxExemptions';
import { supabase } from '@/integrations/supabase/client';

interface TaxExemptionsTabProps {
  organizationId: string;
}

export function TaxExemptionsTab({ organizationId }: TaxExemptionsTabProps) {
  const { data: exemptions, isLoading } = useTaxExemptions(organizationId);
  const createExemption = useCreateTaxExemption();
  const deleteExemption = useDeleteTaxExemption();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    state_codes: [] as string[],
    exemption_type: '',
    certificate_number: '',
    issuing_state: '',
    issue_date: '',
    expiry_date: '',
  });
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const getStatusBadge = (status: TaxExemption['verification_status']) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"><Clock className="h-3 w-3 mr-1" />Pending Review</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'expired':
        return <Badge variant="secondary"><AlertTriangle className="h-3 w-3 mr-1" />Expired</Badge>;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a PDF, JPEG, or PNG file');
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const getSignedUrl = async (filePath: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from('tax-certificates')
      .createSignedUrl(filePath, 3600); // 1 hour expiry
    
    if (error) {
      console.error('Error creating signed URL:', error);
      return null;
    }
    return data.signedUrl;
  };

  const handleViewCertificate = async (documentUrl: string) => {
    const signedUrl = await getSignedUrl(documentUrl);
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    } else {
      alert('Unable to access certificate document');
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || formData.state_codes.length === 0 || !formData.exemption_type) {
      return;
    }

    setIsUploading(true);
    try {
      // Upload file to storage once (shared across all state exemptions)
      const fileExt = selectedFile.name.split('.').pop();
      const stateLabel = formData.state_codes.length === 1 ? formData.state_codes[0] : 'multi-state';
      const filePath = `${organizationId}/${stateLabel}-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('tax-certificates')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      // Create an exemption record for each selected state
      for (const stateCode of formData.state_codes) {
        await createExemption.mutateAsync({
          organization_id: organizationId,
          state_code: stateCode,
          exemption_type: formData.exemption_type,
          certificate_number: formData.certificate_number || undefined,
          issuing_state: formData.issuing_state || undefined,
          issue_date: formData.issue_date || undefined,
          expiry_date: formData.expiry_date || undefined,
          document_url: filePath,
        });
      }

      // Reset form
      setFormData({
        state_codes: [],
        exemption_type: '',
        certificate_number: '',
        issuing_state: '',
        issue_date: '',
        expiry_date: '',
      });
      setSelectedFile(null);
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error('Failed to upload certificate:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteExemption.mutateAsync(id);
    setDeleteConfirmId(null);
  };

  // Check for expiring certificates
  const expiringCertificates = exemptions?.filter(e => {
    if (!e.expiry_date || e.verification_status !== 'verified') return false;
    const daysUntilExpiry = Math.ceil((new Date(e.expiry_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  }) || [];

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Expiring certificates warning */}
      {expiringCertificates.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {expiringCertificates.length === 1 
              ? 'You have a tax exemption certificate expiring within 30 days.'
              : `You have ${expiringCertificates.length} tax exemption certificates expiring within 30 days.`
            }
            {' '}Please upload renewed certificates to maintain your exemptions.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Tax Exemption Certificates</h3>
          <p className="text-sm text-muted-foreground">
            Upload exemption certificates for states where your organization is tax-exempt
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Certificate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Tax Exemption Certificate</DialogTitle>
              <DialogDescription>
                Upload your tax exemption certificate for review by our team
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>State(s) / Jurisdiction(s) *</Label>
                <Popover open={stateDropdownOpen} onOpenChange={setStateDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between font-normal"
                    >
                      {formData.state_codes.length === 0 
                        ? "Select states..." 
                        : formData.state_codes.length === 1
                          ? US_STATES.find(s => s.code === formData.state_codes[0])?.name
                          : `${formData.state_codes.length} states selected`
                      }
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search states..." />
                      <CommandList>
                        <CommandEmpty>No state found.</CommandEmpty>
                        <CommandGroup>
                          {US_STATES.map((state) => (
                            <CommandItem
                              key={state.code}
                              onSelect={() => {
                                const isSelected = formData.state_codes.includes(state.code);
                                if (isSelected) {
                                  setFormData({
                                    ...formData,
                                    state_codes: formData.state_codes.filter(c => c !== state.code)
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    state_codes: [...formData.state_codes, state.code]
                                  });
                                }
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  formData.state_codes.includes(state.code) ? "opacity-100" : "opacity-0"
                                }`}
                              />
                              {state.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {formData.state_codes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.state_codes.map((code) => (
                      <Badge key={code} variant="secondary" className="text-xs">
                        {code}
                        <button
                          type="button"
                          className="ml-1 hover:text-destructive"
                          onClick={() => setFormData({
                            ...formData,
                            state_codes: formData.state_codes.filter(c => c !== code)
                          })}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Select multiple states if you have a multi-state exemption certificate
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Exemption Type *</Label>
                <Select 
                  value={formData.exemption_type} 
                  onValueChange={(v) => setFormData({ ...formData, exemption_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXEMPTION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div>{type.label}</div>
                          <div className="text-xs text-muted-foreground">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cert_number">Certificate Number</Label>
                <Input
                  id="cert_number"
                  value={formData.certificate_number}
                  onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                  placeholder="e.g., 12-3456789"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issue_date">Issue Date</Label>
                  <Input
                    id="issue_date"
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiry_date">Expiry Date</Label>
                  <Input
                    id="expiry_date"
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="file">Certificate Document *</Label>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
                <p className="text-xs text-muted-foreground">
                  PDF, JPEG, or PNG. Max 10MB.
                </p>
              </div>

              <Button 
                onClick={handleSubmit} 
                className="w-full"
                disabled={!selectedFile || formData.state_codes.length === 0 || !formData.exemption_type || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Submit for Review'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Certificates list */}
      {exemptions && exemptions.length > 0 ? (
        <div className="space-y-3">
          {exemptions.map((exemption) => (
            <Card key={exemption.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <FileText className="h-10 w-10 text-muted-foreground p-2 bg-muted rounded" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {US_STATES.find(s => s.code === exemption.state_code)?.name || exemption.state_code}
                        </span>
                        {getStatusBadge(exemption.verification_status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {EXEMPTION_TYPES.find(t => t.value === exemption.exemption_type)?.label}
                        {exemption.certificate_number && ` • #${exemption.certificate_number}`}
                      </p>
                      {exemption.expiry_date && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires: {format(new Date(exemption.expiry_date), 'MMM d, yyyy')}
                        </p>
                      )}
                      {exemption.verification_status === 'rejected' && exemption.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">
                          Reason: {exemption.rejection_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleViewCertificate(exemption.document_url)}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setDeleteConfirmId(exemption.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h4 className="font-medium mb-2">No tax exemption certificates</h4>
            <p className="text-sm text-muted-foreground mb-4">
              If your organization is tax-exempt in certain states, upload your certificates here.
            </p>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Certificate
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Certificate?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the tax exemption certificate. You will be charged tax on future orders to this state unless you upload a new certificate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
